<template>
  <div
    class="el-message-box__wrapper"
    tabindex="-1"
    v-show="visible"
    @click.self="handleWrapperClick"
    role="dialog"
    aria-modal="true"
    :aria-label="title || 'dialog'">
    <div class="el-message-box" :class="[customClass, center && 'el-message-box--center']">
      <div class="el-message-box__content">
        <div class="status-icon" v-if="iconType != null" :class="[{
            'success': iconType == MessageType.SUCCESS,
            'failure':iconType == MessageType.FAIL,
            'info' : iconType == MessageType.INFO}]">
        </div>
        <div class="message-box-title" v-if="title != null && title.length !== 0">{{title}}</div>
        <div class="el-message-box__message align-center-message" v-if="message !== ''">
          <slot>
            <p v-if="!dangerouslyUseHTMLString">{{ message }}</p>
            <p v-else v-html="message"></p>
          </slot>
        </div>
        <div class="el-message-box__input" v-show="showInput">
          <el-input
            v-model="inputValue"
            :type="inputType"
            @keydown.enter.native="handleInputEnter"
            :placeholder="inputPlaceholder"
            ref="input"></el-input>
          <div class="el-message-box__errormsg" :style="{ visibility: !!editorErrorMessage ? 'visible' : 'hidden' }">{{ editorErrorMessage }}</div>
        </div>
      </div>
      <div class="el-message-box__btns">
        <el-button
          :loading="confirmButtonLoading"
          ref="confirm"
          :class="[ confirmButtonClasses ]"
          class="message-box-button"
          v-show="showConfirmButton"
          :round="roundButton"
          size="small"
          @click.native="handleAction('confirm')"
          @keydown.enter="handleAction('confirm')">
          {{ confirmButtonText || t('el.messagebox.confirm') }}
        </el-button>
        <el-button
          :loading="cancelButtonLoading"
          :class="[ cancelButtonClasses ]"
          class="message-box-button"
          v-if="showCancelButton"
          :round="roundButton"
          size="small"
          @click.native="handleAction('cancel')"
          @keydown.enter="handleAction('cancel')">
          {{ cancelButtonText || t('el.messagebox.cancel') }}
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import Popup from 'element-ui/src/utils/popup';
import Locale from 'element-ui/src/mixins/locale';
import ElInput from 'element-ui/packages/input';
import ElButton from 'element-ui/packages/button';
import { addClass, removeClass } from 'element-ui/src/utils/dom';
import { t } from 'element-ui/src/locale';
import Dialog from 'element-ui/src/utils/aria-dialog';
import MessageType from './MessageType';
let messageBox;
let typeMap = {
  success: 'success',
  info: 'info',
  warning: 'warning',
  error: 'error'
};
export default {
  mixins: [Popup, Locale],
  props: {
    iconType: {
      default: 1
    },
    modal: {
      default: true
    },
    lockScroll: {
      default: true
    },
    showClose: {
      type: Boolean,
      default: true
    },
    closeOnClickModal: {
      default: true
    },
    closeOnPressEscape: {
      default: true
    },
    closeOnHashChange: {
      default: true
    },
    center: {
      default: false,
      type: Boolean
    },
    roundButton: {
      default: false,
      type: Boolean
    }
  },
  components: {
    ElInput,
    ElButton
  },
  computed: {
    typeClass() {
      return this.type && typeMap[this.type]
        ? `el-icon-${typeMap[this.type]}`
        : '';
    },
    confirmButtonClasses() {
      return `el-button--primary ${this.confirmButtonClass}`;
    },
    cancelButtonClasses() {
      return `${this.cancelButtonClass}`;
    }
  },
  methods: {
    getSafeClose() {
      const currentId = this.uid;
      return () => {
        this.$nextTick(() => {
          if (currentId === this.uid) this.doClose();
        });
      };
    },
    doClose() {
      if (!this.visible) return;
      this.visible = false;
      this._closing = true;
      this.onClose && this.onClose();
      messageBox.closeDialog(); // 解绑
      if (this.lockScroll) {
        setTimeout(this.restoreBodyStyle, 200);
      }
      this.opened = false;
      this.doAfterClose();
      setTimeout(() => {
        if (this.action) this.callback(this.action, this);
      });
    },
    handleWrapperClick() {
      if (this.closeOnClickModal) {
        this.handleAction('cancel');
      }
    },
    handleInputEnter() {
      if (this.inputType !== 'textarea') {
        return this.handleAction('confirm');
      }
    },
    handleAction(action) {
      if (this.$type === 'prompt' && action === 'confirm' && !this.validate()) {
        return;
      }
      this.action = action;
      if (typeof this.beforeClose === 'function') {
        this.close = this.getSafeClose();
        this.beforeClose(action, this, this.close);
      } else {
        this.doClose();
      }
    },
    validate() {
      if (this.$type === 'prompt') {
        const inputPattern = this.inputPattern;
        if (inputPattern && !inputPattern.test(this.inputValue || '')) {
          this.editorErrorMessage =
            this.inputErrorMessage || t('el.messagebox.error');
          addClass(this.getInputElement(), 'invalid');
          return false;
        }
        const inputValidator = this.inputValidator;
        if (typeof inputValidator === 'function') {
          const validateResult = inputValidator(this.inputValue);
          if (validateResult === false) {
            this.editorErrorMessage =
              this.inputErrorMessage || t('el.messagebox.error');
            addClass(this.getInputElement(), 'invalid');
            return false;
          }
          if (typeof validateResult === 'string') {
            this.editorErrorMessage = validateResult;
            addClass(this.getInputElement(), 'invalid');
            return false;
          }
        }
      }
      this.editorErrorMessage = '';
      removeClass(this.getInputElement(), 'invalid');
      return true;
    },
    getFirstFocus() {
      const btn = this.$el.querySelector('.el-message-box__btns .el-button');
      const title = this.$el.querySelector(
        '.el-message-box__btns .el-message-box__title'
      );
      return btn || title;
    },
    getInputElement() {
      const inputRefs = this.$refs.input.$refs;
      return inputRefs.input || inputRefs.textarea;
    }
  },
  watch: {
    inputValue: {
      immediate: true,
      handler(val) {
        this.$nextTick(_ => {
          if (this.$type === 'prompt' && val !== null) {
            this.validate();
          }
        });
      }
    },
    visible(val) {
      if (val) {
        this.uid++;
        if (this.$type === 'alert' || this.$type === 'confirm') {
          this.$nextTick(() => {
            this.$refs.confirm.$el.focus();
          });
        }
        this.focusAfterClosed = document.activeElement;
        messageBox = new Dialog(
          this.$el,
          this.focusAfterClosed,
          this.getFirstFocus()
        );
      }
      // prompt
      if (this.$type !== 'prompt') return;
      if (val) {
        setTimeout(() => {
          if (this.$refs.input && this.$refs.input.$el) {
            this.getInputElement().focus();
          }
        }, 500);
      } else {
        this.editorErrorMessage = '';
        removeClass(this.getInputElement(), 'invalid');
      }
    }
  },
  mounted() {
    this.$nextTick(() => {
      if (this.closeOnHashChange) {
        window.addEventListener('hashchange', this.close);
      }
    });
  },
  beforeDestroy() {
    if (this.closeOnHashChange) {
      window.removeEventListener('hashchange', this.close);
    }
    setTimeout(() => {
      messageBox.closeDialog();
    });
  },
  data() {
    return {
      MessageType,
      uid: 1,
      title: undefined,
      message: '',
      type: '',
      customClass: '',
      showInput: false,
      inputValue: null,
      inputPlaceholder: '',
      inputType: 'text',
      inputPattern: null,
      inputValidator: null,
      inputErrorMessage: '',
      showConfirmButton: true,
      showCancelButton: false,
      action: '',
      confirmButtonText: '',
      cancelButtonText: '',
      confirmButtonLoading: false,
      cancelButtonLoading: false,
      confirmButtonClass: '',
      confirmButtonDisabled: false,
      cancelButtonClass: '',
      editorErrorMessage: null,
      callback: null,
      dangerouslyUseHTMLString: false,
      focusAfterClosed: null,
      isOnComposition: false
    };
  }
};
</script>
<style lang="less">
.el-message-box__wrapper {
  position: fixed;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  .el-message-box {
    border-radius: 8px;
    box-shadow: 1px 1px 16px 0px rgba(0, 0, 0, 0.16);
    width: 518px;
    padding-bottom: 50px;
    .el-message-box__content {
      padding: 40px 50px 30px 50px;
      .status-icon {
        height: 90px;
        background-position: center top;
        background-repeat: no-repeat;
        &.success {
          background-image: url(../img/message-box/icon_success.png);
        }
        &.failure {
          background-image: url(../img/message-box/icon_failure.png);
        }
        &.info {
          background-image: url(../img/message-box/icon_info.png);
        }
      }
      .message-box-title {
        font-family: HiraginoSansGB-W6;
        font-size: 24px;
        font-weight: normal;
        font-stretch: normal;
        line-height: 24px;
        letter-spacing: 0px;
        color: #333333;
        text-align: center;
        margin-bottom: 25px;
      }
      .el-message-box__message {
        font-size: 16px;
        font-family: "PingFangSC-Regular,'helvetica neue','hiragino sans gb', 'microsoft yahei', tahoma,'microsoft yahei ui', simsun,sans-serif;";
        word-break: break-all;
        text-align: left;
        &.align-center-message {
          text-align: center;
        }
      }
    }
    .el-message-box__btns {
      text-align: center;
      .message-box-button {
        border-radius: 5px;
        height: 40px;
        min-width: 190px;
        font-size: 15px;
      }
      .el-button {
        border-color: #bfddff;
        color: #5c9dff;
        &.el-button--primary {
          color: #ffffff;
          border: none;
        }
      }
      button:nth-child(2) {
        margin-left: 30px;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/common/message-box/main.vue